import jxon from '@infomaker/im-jxon'

import {Events} from './Events'
import {Document} from './Document'
import {NewsItem} from './NewsItem'
import {Upload} from './Upload'
import {Article} from './Article'
import {Browser} from './Browser'
import {Router} from './Router'
import {Concept} from './Concept'
import {Ui} from './Ui'
import {History} from './History'
import {Settings} from './Settings'

/** @ignore */
jxon.config({
    autoDate: false,
    parseValues: false
})

/**
 * @class
 * @classdesc Top level api object from which all other api modules are available.
 */
class Api {
    /**
     * @hideconstructor
     */
    constructor({configurator, pluginConfigurations = null, newsItem, temporaryArticleId}) {

        // TODO: Deprecate this? Should be fetched via context in plugins instead
        configurator.expose('api', this)

        /** @private */
        this._temporaryArticleId = temporaryArticleId

        /** @private */
        this._stateHandler = null

        /** @private */
        this._editorSession = null

        /** @private */
        this.resourceManager = null

        /** @private */
        this._newsItem = newsItem

        /** @private */
        this.configurator = configurator

        /** @private */
        this._pluginConfigurations = pluginConfigurations

        this.eventListeners = []

        /**
         * @property {object} article
         * @see {@link Article.html}
         */
        this.article = new Article(this)

        /**
         * @property {Document} document
         * @see {@link Document.html}
         */
        this.document = new Document(this)

        /**
         * @property {NewsItem} newsItem
         * @see {@link NewsItem.html}
         */
        this.newsItem = new NewsItem(this)

        /**
         * @property {Events} events
         * @see {@link Events.html}
         */
        this.events = new Events()

        /**
         * @property {Router} router
         * @see {@link Router.html}
         */
        this.router = new Router(this)

        /**
         * @property {Browser} browser
         * @see {@link Browser.html}
         */
        this.browser = new Browser(this)

        /**
         * @property {Ui} api.ui
         * @see {@link Ui.html}
         */
        this.ui = new Ui(this)

        /**
         * @property {Upload} api.upload
         * @see {@link Upload.html}
         */
        this.upload = new Upload(this)

        /**
         * @deprecated 2018-12-11
         */
        this.concept = Concept

        /**
         * @property {History} api.history
         * @see {@link History.html}
         */
        this.history = new History(this)

        /**
         * @property {Settings} api.settings
         * @see {@link Settings.html}
         */
        this.settings = new Settings('__settings__')
    }

    setModalReference(modal) {
        this.modal = modal
    }

    setResourceManager(resourceManager) {
        this.resourceManager = resourceManager
    }

    /**
     * Property getter to retrieve reference to editorSession
     *
     * @return {Object} Substance editorSession
     */
    get editorSession() {
        try {
            return this._editorSession
        }
        catch(ex) {
            console.error(`Object editorSession not available`)
            throw(ex)
        }
    }

    /**
     * Property setter to store reference to editorSession
     *
     * @param {Object} editorSession Substance editorSession
     */
    set editorSession(editorSession) {
        this._editorSession = editorSession
    }

    /**
     * Actually newsItem
     *
     * @returns {*}
     */
    get newsItemArticle() {
        return this._newsItem
    }

    set newsItemArticle(newsItem) {
        this._newsItem = newsItem
    }

    /**
     * Property getter to retrieve reference to the StateHandler
     *
     * @return {Object} StateHandler
     */
    get stateHandler() {
        try {
            return this._stateHandler
        }
        catch(ex) {
            console.error(`Object stateHandler not available`)
            throw(ex)
        }
    }

    /**
     * Property setter to store reference to a StateHandler
     *
     * @param {Object} stateHandler
     */
    set stateHandler(stateHandler) {
        this._stateHandler = stateHandler
    }

    /**
     * Property getter for the editorSession document
     *
     * @return {Object} Substance document
     */
    get doc() {
        try {
            return this.editorSession.getDocument()
        }
        catch(ex) {
            console.error(`Could not retrieve document from editorSession`)
            throw(ex)
        }
    }

    /**
     * Get current version of the Writer
     *
     * @return {string} Version tag
     */
    getVersion() {
        return this.router.get('/api/versions')
            .then(response => {
                if (response.status !== 200) {
                    throw new Error(`/api/versions returned ${response.status}`)
                }
                return response
            })
            .then(response => response.json())
            .then(json => {
                return json.versioninfo.tag
            })
            .catch(err => {
                throw err
            })
    }

    /**
     * Get configuration value in a plugins local configuration data section.
     * If no path is given the full configuration object for the plugin instance
     * is returned.
     *
     * @example
     * {
     *    "vendor": "vendor.tld",
     *    "name": "myplugin",
     *    "enabled": true,
     *    "data": {
     *      "mykey": "Configuration value"
     *    }
     *  },
     * @example
     *
     * import {api} from 'writer'
     * var apiEndpoint = api.getConfigValue('myplugin', 'mykey');`
     *
     * @param {string} id Plugin id to fetch configuration value for
     * @param {string} path Optional configuration path in the data section
     * @param {*} defaultValue Optional default value if no value is found
     * @return {*} The value of the wanted configuration path or default value
     */
    getConfigValue(id, path, defaultValue) {
        return this._pluginConfigurations.getConfigValue(id, path, defaultValue)
    }

    /**
     * Check if the configuration has been overridden with local configuration
     * @returns {Boolean}
     */
    hasLocalConfiguration() {
        return this.settings.get('__global__', 'localConfiguration', false) !== false
    }

    getLabel(name) {
        var labelProvider = this.configurator.labelProvider
        if (!labelProvider) {
            throw new Error('Missing labelProvider.')
        }
        return labelProvider.getLabel(name)
    }


    /**
     * Return a fresh instance of a specific plugin provided module
     *
     * @param {string} moduleId - Module id
     *
     * @return {Object}
     */
    getPluginModule(moduleId) {
        return this.configurator.getPluginModule(moduleId)
    }

    /**
     * Get modules targeted at a specific consumer
     *
     * @param {string} targetId - Target id of the consumer
     */
    getPluginModulesForTarget(targetId) {
        return this.configurator.getPluginModulesForTarget(targetId)
    }
}

export {Api}
